import React from 'react';
import { getBallsThrown } from '../utils/statistika';

const KLUBOVI = ['KK Rad', 'KK Rad 2', 'ŽKK Olimp'];

function Pocetna({ igraci, utakmice, treninzi, turniri, setAktivna, globalnaSezona, jeAdmin, jeGost }) {
  const utakmiceSezone = utakmice.filter(u => u.sezona === globalnaSezona);

  // Per-club stats
  const klubStats = KLUBOVI.map(klub => {
    const klubUtakmice = utakmiceSezone
      .filter(u => u.klub === klub)
      .sort((a, b) => b.datum?.localeCompare(a.datum));

    const ukupno = klubUtakmice.length;
    const pobjede = klubUtakmice.filter(u => u.ekipaBodovi === 2).length;
    const remiji = klubUtakmice.filter(u => u.ekipaBodovi === 1).length;
    const porazi = klubUtakmice.filter(u => u.ekipaBodovi === 0).length;
    const bodovi = klubUtakmice.reduce((s, u) => s + (u.ekipaBodovi || 0), 0);
    const prosjecniCunjevi = ukupno > 0
      ? Math.round(klubUtakmice.reduce((s, u) => s + (u.ekipaUkupno || 0), 0) / ukupno)
      : 0;
    const prosjecniMP = ukupno > 0
      ? (klubUtakmice.reduce((s, u) => s + (u.ekipaMP || 0), 0) / ukupno).toFixed(1)
      : 0;
    const forma = klubUtakmice.slice(0, 5).map(u => u.ekipaBodovi);
    const igraciKluba = igraci.filter(i => i.klub === klub).length;
    const najvisiRezultat = ukupno > 0
      ? Math.max(...klubUtakmice.map(u => u.ekipaUkupno || 0))
      : 0;

    return { klub, ukupno, pobjede, remiji, porazi, bodovi, prosjecniCunjevi, prosjecniMP, forma, igraciKluba, najvisiRezultat };
  });

  // Top 3 po klubu — normalizovani prosjek (120 ekvivalent), per-klub računanje
  const topBacaciPoKlubu = KLUBOVI.map(klub => {
    const klubUtakmice = utakmiceSezone.filter(u => u.klub === klub);

    // Skupi sve igrače koji su nastupali za ovaj klub
    const igracIdsSet = new Set();
    klubUtakmice.forEach(u => (u.rezultati || []).forEach(r => { if (r.igracId) igracIdsSet.add(r.igracId); }));

    const saProsjekom = [...igracIdsSet].map(igracId => {
      const igracObj = igraci.find(i => i.id === igracId);
      const prvaUtakmica = klubUtakmice.find(u => u.rezultati?.some(r => r.igracId === igracId));
      const prviRez = prvaUtakmica?.rezultati?.find(r => r.igracId === igracId);
      const ime = igracObj?.ime || prviRez?.ime || 'Nepoznat';

      let ukupnoCunjeva = 0, ukupnoKugli = 0, nastupa = 0, najboljiBacaj = 0;
      klubUtakmice.forEach(u => {
        const r = (u.rezultati || []).find(r => r.igracId === igracId);
        if (!r) return;
        nastupa++;
        const cunjevi = r.ukupno || 0;
        ukupnoCunjeva += cunjevi;
        if (cunjevi > najboljiBacaj) najboljiBacaj = cunjevi;
        ukupnoKugli += getBallsThrown(r);
      });

      if (nastupa < 2) return null;
      const prosjek = ukupnoKugli > 0 ? Math.round((ukupnoCunjeva / ukupnoKugli) * 120) : 0;
      return { id: igracId, ime, utakmice: nastupa, prosjek, najboljiBacaj };
    }).filter(Boolean);

    return {
      klub,
      igraci: saProsjekom.sort((a, b) => b.prosjek - a.prosjek).slice(0, 3),
    };
  });

  // Per-player stats za rekordi sekciju (sve utakmice, po klubu odvojeno)
  const igracStatistike = {};
  utakmiceSezone.forEach(utakmica => {
    (utakmica.rezultati || []).forEach(r => {
      if (!r.igracId) return;
      const key = r.igracId + '_' + utakmica.klub;
      if (!igracStatistike[key]) {
        igracStatistike[key] = {
          id: r.igracId,
          ime: igraci.find(i => i.id === r.igracId)?.ime || r.ime || 'Nepoznat',
          klub: utakmica.klub,
          ukupno: 0,
          utakmice: 0,
          sp: 0,
          mp: 0,
          promasaji: 0,
          najboljiBacaj: 0,
        };
      }
      const st = igracStatistike[key];
      st.ukupno += r.ukupno || 0;
      st.utakmice += 1;
      st.sp += r.sp || 0;
      st.mp += r.mp || 0;
      st.promasaji += r.promasaji || 0;
      if ((r.ukupno || 0) > st.najboljiBacaj) st.najboljiBacaj = r.ukupno;
    });
  });

  // Season totals
  const ukupnoUtakmica = utakmiceSezone.length;
  const ukupnoPobjeda = utakmiceSezone.filter(u => u.ekipaBodovi === 2).length;

  // Timski rekordi — po klubu
  const timskiRekordPoKlubu = KLUBOVI.map(klub => {
    const uk = utakmiceSezone.filter(u => u.klub === klub);
    const bestCunjevi = uk.reduce((b, u) => (u.ekipaUkupno || 0) > (b?.ekipaUkupno || 0) ? u : b, null);
    const bestMP = uk.reduce((b, u) => (u.ekipaMP || 0) > (b?.ekipaMP || 0) ? u : b, null);
    const bestSP = uk.reduce((b, u) => (u.ekipaSP || 0) > (b?.ekipaSP || 0) ? u : b, null);
    const bestPromasaji = uk.filter(u => (u.ekipaPromasaji || 0) > 0)
      .reduce((b, u) => b === null || (u.ekipaPromasaji || 0) < (b?.ekipaPromasaji || 999) ? u : b, null);
    return { klub, bestCunjevi, bestMP, bestSP, bestPromasaji };
  });

  // Individualni rekordi — po klubu
  const individRekordPoKlubu = KLUBOVI.map(klub => {
    const igraciByCunjevi = {}, igraciBySP = {}, igraciBySPutakmica = {}, igracsByPromasaji = {};

    utakmiceSezone.filter(u => u.klub === klub).forEach(u => {
      (u.rezultati || []).forEach(r => {
        if (!r.igracId) return;
        if (!igraciByCunjevi[r.igracId] || (r.ukupno || 0) > igraciByCunjevi[r.igracId].vrijednost)
          igraciByCunjevi[r.igracId] = { igracId: r.igracId, ime: r.ime, vrijednost: r.ukupno || 0, utakmicaId: u.id };
        if (!igraciBySP[r.igracId] || (r.sp || 0) > igraciBySP[r.igracId].vrijednost)
          igraciBySP[r.igracId] = { igracId: r.igracId, ime: r.ime, vrijednost: r.sp || 0, utakmicaId: u.id };
        if ((r.promasaji || 0) > 0 && (!igracsByPromasaji[r.igracId] || (r.promasaji || 0) < igracsByPromasaji[r.igracId].vrijednost))
          igracsByPromasaji[r.igracId] = { igracId: r.igracId, ime: r.ime, vrijednost: r.promasaji || 0, utakmicaId: u.id };
      });
    });

    const bestCunjevi = Object.values(igraciByCunjevi).sort((a, b) => b.vrijednost - a.vrijednost)[0] || null;
    const bestSP = Object.values(igraciBySP).sort((a, b) => b.vrijednost - a.vrijednost)[0] || null;
    const bestPromasaji = Object.values(igracsByPromasaji).sort((a, b) => a.vrijednost - b.vrijednost)[0] || null;

    const sveStatistike = Object.values(igracStatistike).filter(st => st.klub === klub);
    const bestProsjek = sveStatistike.filter(st => st.utakmice >= 2)
      .map(st => ({ ...st, prosjek: Math.round(st.ukupno / st.utakmice) }))
      .sort((a, b) => b.prosjek - a.prosjek)[0] || null;
    const bestMP = [...sveStatistike].sort((a, b) => b.mp - a.mp)[0] || null;

    return { klub, bestCunjevi, bestSP, bestPromasaji, bestProsjek, bestMP };
  });

  // Top turnirski nastupnici — sve sezone
  const turniriBrojac = {};
  (turniri || []).forEach(t => {
    (t.nastupnici || []).forEach(n => {
      if (!n.igracId) return;
      if (!turniriBrojac[n.igracId]) turniriBrojac[n.igracId] = { igracId: n.igracId, ime: n.ime, ukupno: 0 };
      turniriBrojac[n.igracId].ukupno += 1;
    });
  });
  const topTurnirasi = Object.values(turniriBrojac).sort((a, b) => b.ukupno - a.ukupno).slice(0, 3);

  const formaColor = (bodovi) => {
    if (bodovi === 2) return '#2ed573';
    if (bodovi === 1) return '#5378ff';
    return '#e94560';
  };
  const formaLabel = (bodovi) => {
    if (bodovi === 2) return 'P';
    if (bodovi === 1) return 'R';
    return 'G';
  };

  const medalja = (i) => {
    if (i === 0) return { emoji: '🥇', color: '#ffd700' };
    if (i === 1) return { emoji: '🥈', color: '#c0c0c0' };
    if (i === 2) return { emoji: '🥉', color: '#cd7f32' };
    return { emoji: null, color: '#e94560' };
  };

  return (
    <div>
      <div className="page-title" style={{ marginBottom: '6px' }}>
        🎳 <span>Arena</span> Bakarni
      </div>
      <div style={{ color: '#666688', fontSize: '13px', marginBottom: '24px' }}>
        Sezona {globalnaSezona} • {ukupnoUtakmica} utakmica • {ukupnoPobjeda} pobjeda
      </div>

      {/* Club cards */}
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '28px' }}>
        {klubStats.map(ks => (
          <div key={ks.klub} className="card"
            onClick={() => setAktivna({ strana: 'ranglista', klub: ks.klub })}
            style={{ flex: 1, minWidth: '220px', cursor: 'pointer', borderLeft: '3px solid #e94560', padding: '16px 18px' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
              <div style={{ fontSize: '15px', fontWeight: '700', color: '#fff' }}>{ks.klub}</div>
              <div style={{ fontSize: '12px', color: '#666688' }}>{ks.igraciKluba} igrača</div>
            </div>

            {/* W/D/L record */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#2ed573' }}>{ks.pobjede}</div>
                <div style={{ fontSize: '10px', color: '#666688', textTransform: 'uppercase' }}>Pob</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#5378ff' }}>{ks.remiji}</div>
                <div style={{ fontSize: '10px', color: '#666688', textTransform: 'uppercase' }}>Rem</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#e94560' }}>{ks.porazi}</div>
                <div style={{ fontSize: '10px', color: '#666688', textTransform: 'uppercase' }}>Por</div>
              </div>
              <div style={{ width: '1px', background: '#2a2a4a', margin: '0 4px' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: '700', color: '#fff' }}>{ks.bodovi}</div>
                <div style={{ fontSize: '10px', color: '#666688', textTransform: 'uppercase' }}>Bod</div>
              </div>
            </div>

            {/* Averages */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '14px', fontSize: '13px' }}>
              <div>
                <span style={{ color: '#666688' }}>Ø čunjeva: </span>
                <span style={{ color: '#fff', fontWeight: '600' }}>{ks.prosjecniCunjevi || '—'}</span>
              </div>
              <div>
                <span style={{ color: '#666688' }}>Ø MP: </span>
                <span style={{ color: '#fff', fontWeight: '600' }}>{ks.prosjecniMP || '—'}</span>
              </div>
            </div>

            {/* Form strip */}
            {ks.forma.length > 0 && (
              <div>
                <div style={{ fontSize: '10px', color: '#666688', textTransform: 'uppercase', marginBottom: '6px' }}>
                  Forma (zadnjih {ks.forma.length})
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {ks.forma.map((bod, i) => (
                    <div key={i} style={{
                      width: '26px', height: '26px', borderRadius: '4px',
                      background: formaColor(bod),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: '700', color: '#fff',
                    }}>
                      {formaLabel(bod)}
                    </div>
                  ))}
                  {[...Array(Math.max(0, 5 - ks.forma.length))].map((_, i) => (
                    <div key={'empty-' + i} style={{
                      width: '26px', height: '26px', borderRadius: '4px',
                      background: '#1a1a2e', border: '1px solid #2a2a4a',
                    }} />
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: '12px', fontSize: '12px', color: '#e94560' }}>Rang lista →</div>
          </div>
        ))}
      </div>

      {/* Najbolji igrači — tabela po klubovima */}
      <div style={{ marginBottom: '28px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div className="section-title" style={{ margin: 0 }}>Najbolji igrači — {globalnaSezona}</div>
          <button className="btn-secondary btn-sm" onClick={() => setAktivna({ strana: 'ranglista' })}>Sve →</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 6px' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'center', padding: '6px 10px', fontSize: '11px', color: '#666688', fontWeight: '500', width: '40px' }}>#</th>
                {KLUBOVI.map(k => (
                  <th key={k} style={{ textAlign: 'left', padding: '6px 14px', fontSize: '11px', color: '#aaa', fontWeight: '600' }}>{k}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[0, 1, 2].map(rank => {
                const m = medalja(rank);
                return (
                  <tr key={rank} style={{ background: '#12122a' }}>
                    <td style={{ textAlign: 'center', padding: '10px 6px', borderRadius: '6px 0 0 6px' }}>
                      {m.emoji ? <span style={{ fontSize: '18px' }}>{m.emoji}</span>
                        : <span style={{ fontWeight: '700', color: m.color }}>{rank + 1}.</span>}
                    </td>
                    {KLUBOVI.map(k => {
                      const klub = topBacaciPoKlubu.find(t => t.klub === k);
                      const st = klub?.igraci[rank];
                      return (
                        <td key={k} style={{ padding: '10px 14px', cursor: st ? 'pointer' : 'default' }}
                          onClick={() => st && setAktivna({ strana: 'profil', igracId: st.id })}>
                          {st ? (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                              <div>
                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#fff' }}>{st.ime}</div>
                                <div style={{ fontSize: '11px', color: '#666688', marginTop: '2px' }}>
                                  {st.utakmice} nast. • best {st.najboljiBacaj}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <div style={{ fontSize: '20px', fontWeight: '700', color: m.color }}>{st.prosjek}</div>
                                <div style={{ fontSize: '10px', color: '#666688' }}>prosjek</div>
                              </div>
                            </div>
                          ) : <span style={{ color: '#333355' }}>—</span>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sezonski rekordi */}
      {utakmiceSezone.length > 0 && (
        <div style={{ marginTop: '28px' }}>
          <div className="section-title">Sezonski rekordi — {globalnaSezona}</div>

          {/* Ekipni rekordi — tabela po klubovima */}
          <div style={{ fontSize: '12px', color: '#666688', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', marginTop: '4px' }}>
            Ekipni rekordi
          </div>
          <div style={{ overflowX: 'auto', marginBottom: '24px' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 6px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 14px', fontSize: '11px', color: '#666688', fontWeight: '500', width: '30%' }}>Rekord</th>
                  {KLUBOVI.map(k => (
                    <th key={k} style={{ textAlign: 'center', padding: '6px 10px', fontSize: '11px', color: '#aaa', fontWeight: '600' }}>{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { naziv: '🎳 Najviše čunjeva', kljuc: 'bestCunjevi', polje: 'ekipaUkupno', boja: '#ffc107' },
                  { naziv: '⚡ Najviše MP', kljuc: 'bestMP', polje: 'ekipaMP', boja: '#5378ff' },
                  { naziv: '🎯 Najviše SP', kljuc: 'bestSP', polje: 'ekipaSP', boja: '#2ed573' },
                  { naziv: '✅ Najmanje promašaja', kljuc: 'bestPromasaji', polje: 'ekipaPromasaji', boja: '#2ed573', manji: true },
                ].map(red => (
                  <tr key={red.naziv} style={{ background: '#12122a' }}>
                    <td style={{ padding: '10px 14px', fontSize: '12px', color: '#999', borderRadius: '6px 0 0 6px' }}>{red.naziv}</td>
                    {timskiRekordPoKlubu.map(tk => {
                      const u = tk[red.kljuc];
                      const val = u?.[red.polje];
                      return (
                        <td key={tk.klub} style={{ padding: '10px', textAlign: 'center' }}
                          onClick={() => u?.id && setAktivna({ strana: 'detaljiUtakmice', utakmicaId: u.id })}>
                          {val != null && val > 0 ? (
                            <div style={{ cursor: u?.id ? 'pointer' : 'default' }}>
                              <div style={{ fontSize: '20px', fontWeight: '700', color: red.boja }}>{val}</div>
                              <div style={{ fontSize: '10px', color: '#555577', marginTop: '2px' }}>vs {u.protivnik}</div>
                              <div style={{ fontSize: '10px', color: '#444466' }}>{u.datum}</div>
                            </div>
                          ) : <span style={{ color: '#333355' }}>—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Individualni rekordi — tabela po klubovima */}
          <div style={{ fontSize: '12px', color: '#666688', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
            Individualni rekordi
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 6px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 14px', fontSize: '11px', color: '#666688', fontWeight: '500', width: '30%' }}>Rekord</th>
                  {KLUBOVI.map(k => (
                    <th key={k} style={{ textAlign: 'center', padding: '6px 10px', fontSize: '11px', color: '#aaa', fontWeight: '600' }}>{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { naziv: '📊 Prosjek sezone', kljuc: 'bestProsjek', render: (d) => d ? { val: d.prosjek, sub: `${d.utakmice} utakmica`, id: d.id, ime: d.ime } : null, boja: '#e94560', tip: 'igrac' },
                  { naziv: '🎳 Najviše čunjeva (1 utakmica)', kljuc: 'bestCunjevi', render: (d) => d ? { val: d.vrijednost, sub: '', id: d.igracId, ime: d.ime, utakmicaId: d.utakmicaId } : null, boja: '#ffc107', tip: 'igrac' },
                  { naziv: '⚡ Najviše MP sezone', kljuc: 'bestMP', render: (d) => d ? { val: d.mp?.toFixed(1), sub: `${d.utakmice} utakmica`, id: d.id, ime: d.ime } : null, boja: '#5378ff', tip: 'igrac' },
                ].map(red => (
                  <tr key={red.naziv} style={{ background: '#12122a' }}>
                    <td style={{ padding: '10px 14px', fontSize: '12px', color: '#999', borderRadius: '6px 0 0 6px' }}>{red.naziv}</td>
                    {individRekordPoKlubu.map(ik => {
                      const d = red.render(ik[red.kljuc]);
                      return (
                        <td key={ik.klub} style={{ padding: '10px', textAlign: 'center' }}
                          onClick={() => d?.id && setAktivna({ strana: 'profil', igracId: d.id })}>
                          {d && d.val != null && d.val > 0 ? (
                            <div style={{ cursor: d.id ? 'pointer' : 'default' }}>
                              <div style={{ fontSize: '20px', fontWeight: '700', color: red.boja }}>{d.val}</div>
                              <div style={{ fontSize: '12px', color: '#fff', marginTop: '3px', fontWeight: '500' }}>{d.ime}</div>
                              {d.sub && <div style={{ fontSize: '10px', color: '#555577', marginTop: '1px' }}>{d.sub}</div>}
                            </div>
                          ) : <span style={{ color: '#333355' }}>—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Turnirski nastupnici — dno stranice */}
      {topTurnirasi.length > 0 && (
        <div style={{ marginTop: '28px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div className="section-title" style={{ margin: 0 }}>🏆 Najaktivniji na turnirima</div>
            <button className="btn-secondary btn-sm" onClick={() => setAktivna({ strana: 'turniri' })}>Svi turniri →</button>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {topTurnirasi.map((n, i) => {
              const m = medalja(i);
              const igrac = igraci.find(ig => ig.id === n.igracId);
              return (
                <div key={n.igracId}
                  style={{ flex: 1, minWidth: '140px', padding: '16px 12px', background: '#12122a', borderRadius: '10px', border: '1px solid ' + (i === 0 ? '#ffd700' : '#2a2a4a'), textAlign: 'center', cursor: 'pointer' }}
                  onClick={() => setAktivna({ strana: 'profil', igracId: n.igracId })}>
                  <div style={{ fontSize: '28px', marginBottom: '6px' }}>{m.emoji}</div>
                  <div style={{ fontSize: '30px', fontWeight: '700', color: m.color, lineHeight: 1 }}>{n.ukupno}</div>
                  <div style={{ fontSize: '10px', color: '#666688', textTransform: 'uppercase', margin: '4px 0 8px', letterSpacing: '1px' }}>nastupa</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#fff' }}>{n.ime}</div>
                  {igrac && <div style={{ fontSize: '11px', color: '#666688', marginTop: '3px' }}>{igrac.klub}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default Pocetna;
